import React, { useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ResearchLibrary = ({ user, onUseResearch }) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  const [analyses, setAnalyses] = useState([]);
  const [consolidated, setConsolidated] = useState(null);
  const [savedLibraries, setSavedLibraries] = useState([]);
  const [activeLibrary, setActiveLibrary] = useState(null);
  const [libraryName, setLibraryName] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const fileInputRef = useRef(null);

  const loadLibraries = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/research/libraries`, { withCredentials: true });
      if (res.data.success) {
        setSavedLibraries(res.data.libraries || []);
      }
    } catch (err) {
      logger.error('Error loading libraries:', err);
    }
  }, []);

  // Load saved libraries on mount
  useEffect(() => {
    loadLibraries();
  }, [loadLibraries]);

  const handleDrag = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    const files = Array.from(e.dataTransfer.files).filter(f => {
      const ext = f.name.split('.').pop().toLowerCase();
      return ['pdf', 'ppt', 'pptx'].includes(ext);
    });
    if (files.length > 0) {
      setUploadedFiles(prev => [...prev, ...files]);
    }
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    setUploadedFiles(prev => [...prev, ...files]);
  };

  const removeFile = (index) => {
    setUploadedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const analyzeDocuments = async () => {
    if (uploadedFiles.length === 0) return;
    setAnalyzing(true);
    setError(null);

    try {
      const formData = new FormData();
      uploadedFiles.forEach(file => formData.append('files', file));

      const res = await axios.post(`${API}/research/analyze-documents`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });

      if (res.data.success) {
        setAnalyses(res.data.analyses || []);
        setConsolidated(res.data.consolidated || null);
      } else {
        setError('Analysis failed. Please try again.');
      }
    } catch (err) {
      logger.error('Research analysis error:', err);
      setError(err.response?.data?.detail || 'Analysis failed. Please try again.');
    } finally {
      setAnalyzing(false);
    }
  };

  const saveLibrary = async () => {
    try {
      const name = libraryName || `Research Library ${new Date().toLocaleDateString()}`;
      const res = await axios.post(`${API}/research/save`, {
        name,
        analyses,
        consolidated,
      }, { withCredentials: true });

      if (res.data.success) {
        setLibraryName('');
        loadLibraries();
        alert('Research library saved.');
      }
    } catch (err) {
      logger.error('Error saving library:', err);
      alert('Failed to save library.');
    }
  };

  const loadLibrary = async (id) => {
    try {
      const res = await axios.get(`${API}/research/library/${id}`, { withCredentials: true });
      if (res.data.success) {
        setActiveLibrary(res.data.library);
        setAnalyses(res.data.library.documents || []);
        setConsolidated(res.data.library.consolidated || null);
      }
    } catch (err) {
      logger.error('Error loading library:', err);
    }
  };

  const handleUseResearch = () => {
    if (onUseResearch && consolidated) {
      onUseResearch({ analyses, consolidated });
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  };

  return (
    <div className="flex gap-6 h-full" data-testid="research-library">
      {/* Left sidebar - Saved Libraries */}
      <div className="w-72 flex-shrink-0">
        <div className="bg-white rounded-lg shadow-md p-4">
          <h3 className="text-sm font-bold text-gray-900 mb-3 uppercase tracking-wider">Saved Libraries</h3>
          {savedLibraries.length === 0 ? (
            <p className="text-sm text-gray-500">No saved libraries yet.</p>
          ) : (
            <div className="space-y-2">
              {savedLibraries.map((lib) => (
                <button
                  key={lib.id}
                  onClick={() => loadLibrary(lib.id)}
                  className={`w-full text-left p-3 rounded-lg border transition-colors ${
                    activeLibrary?.id === lib.id
                      ? 'border-orange-400 bg-orange-50'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                  data-testid={`library-${lib.id}`}
                >
                  <p className="text-sm font-medium text-gray-900 truncate">{lib.name}</p>
                  <p className="text-xs text-gray-500 mt-1">
                    {lib.documents?.length || 0} documents
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 space-y-6">
        {/* Upload Section */}
        {analyses.length === 0 && (
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-bold text-gray-900 mb-4">Upload Research Documents</h2>
            <p className="text-sm text-gray-600 mb-4">
              Upload PDF or PowerPoint files to extract market intelligence, competitive data, and strategic insights using AI.
            </p>

            {/* Drop zone */}
            <div
              className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer ${
                dragActive
                  ? 'border-orange-400 bg-orange-50'
                  : 'border-gray-300 hover:border-gray-400'
              }`}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
              data-testid="drop-zone"
            >
              <svg className="w-12 h-12 mx-auto text-gray-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="text-sm font-medium text-gray-700">Drag & drop files here, or click to browse</p>
              <p className="text-xs text-gray-500 mt-1">PDF, PPT, PPTX — up to 50MB each</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.ppt,.pptx"
                onChange={handleFileSelect}
                className="hidden"
                data-testid="file-input"
              />
            </div>

            {/* File list */}
            {uploadedFiles.length > 0 && (
              <div className="mt-4 space-y-2">
                {uploadedFiles.map((file, i) => (
                  <div key={`${file.name}-${file.size}-${i}`} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg" data-testid={`file-${i}`}>
                    <div className="flex items-center gap-3">
                      <svg className="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <div>
                        <p className="text-sm font-medium text-gray-900">{file.name}</p>
                        <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                      </div>
                    </div>
                    <button
                      onClick={() => removeFile(i)}
                      className="text-gray-400 hover:text-red-500 transition-colors"
                      data-testid={`remove-file-${i}`}
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                ))}

                <button
                  onClick={analyzeDocuments}
                  disabled={analyzing}
                  className="w-full mt-4 bg-orange-500 text-white py-3 rounded-lg font-semibold hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="analyze-btn"
                >
                  {analyzing ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Analyzing with Gemini AI...
                    </span>
                  ) : (
                    `Analyze ${uploadedFiles.length} Document${uploadedFiles.length > 1 ? 's' : ''}`
                  )}
                </button>
              </div>
            )}

            {error && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700" data-testid="error-message">
                {error}
              </div>
            )}
          </div>
        )}

        {/* Analysis Results */}
        {analyses.length > 0 && (
          <>
            {/* Action bar */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900">
                Research Analysis — {analyses.length} Document{analyses.length > 1 ? 's' : ''}
              </h2>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={libraryName}
                  onChange={(e) => setLibraryName(e.target.value)}
                  placeholder="Library name..."
                  className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-orange-500 focus:border-orange-500"
                  data-testid="library-name-input"
                />
                <button
                  onClick={saveLibrary}
                  className="px-4 py-2 bg-gray-900 text-white rounded-lg text-sm font-medium hover:bg-gray-800 transition-colors"
                  data-testid="save-library-btn"
                >
                  Save Library
                </button>
                <button
                  onClick={handleUseResearch}
                  className="px-4 py-2 bg-orange-500 text-white rounded-lg text-sm font-medium hover:bg-orange-600 transition-colors"
                  data-testid="use-research-btn"
                >
                  Use This Research
                </button>
                <button
                  onClick={() => { setAnalyses([]); setConsolidated(null); setUploadedFiles([]); setActiveLibrary(null); }}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
                  data-testid="new-research-btn"
                >
                  New Research
                </button>
              </div>
            </div>

            {/* Consolidated Summary */}
            {consolidated && (
              <div className="bg-white rounded-lg shadow-md p-6" data-testid="consolidated-summary">
                <h3 className="text-base font-bold text-gray-900 mb-4">Research Summary</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                  <div className="bg-orange-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-orange-600">{consolidated.successful_analyses || 0}</p>
                    <p className="text-xs text-gray-600">Documents Analyzed</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-blue-600">{consolidated.total_findings || 0}</p>
                    <p className="text-xs text-gray-600">Key Findings</p>
                  </div>
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-green-600">{consolidated.company_count || 0}</p>
                    <p className="text-xs text-gray-600">Companies Found</p>
                  </div>
                  <div className="bg-purple-50 rounded-lg p-3 text-center">
                    <p className="text-2xl font-bold text-purple-600">{consolidated.trends_identified || 0}</p>
                    <p className="text-xs text-gray-600">Trends Identified</p>
                  </div>
                </div>

                {/* Top findings */}
                {consolidated.top_findings?.length > 0 && (
                  <div>
                    <h4 className="text-sm font-semibold text-gray-700 mb-2">Top Findings</h4>
                    <ul className="space-y-1">
                      {consolidated.top_findings.slice(0, 5).map((finding, i) => (
                        <li key={`finding-${i}-${finding.slice(0, 20)}`} className="flex items-start gap-2 text-sm text-gray-700">
                          <span className="text-orange-500 mt-0.5">&#x2022;</span>
                          <span>{finding}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}

            {/* Individual Document Cards */}
            {analyses.map((doc, index) => (
              <DocumentCard key={doc.filename || `doc-${index}`} doc={doc} index={index} />
            ))}
          </>
        )}
      </div>
    </div>
  );
};


const DocumentCard = ({ doc, index }) => {
  const [expanded, setExpanded] = useState(false);

  if (doc.error) {
    return (
      <div className="bg-white rounded-lg shadow-md p-5 border-l-4 border-red-400" data-testid={`doc-card-${index}`}>
        <p className="text-sm font-medium text-red-700">Failed to analyze: {doc.filename}</p>
        <p className="text-xs text-red-500 mt-1">{doc.error}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden" data-testid={`doc-card-${index}`}>
      {/* Header */}
      <div
        className="p-5 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="px-2 py-0.5 bg-orange-100 text-orange-700 text-xs font-medium rounded">
                {doc.document_type || 'Document'}
              </span>
              {doc.data_quality?.confidence && (
                <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                  doc.data_quality.confidence === 'High'
                    ? 'bg-green-100 text-green-700'
                    : doc.data_quality.confidence === 'Medium'
                    ? 'bg-yellow-100 text-yellow-700'
                    : 'bg-gray-100 text-gray-700'
                }`}>
                  {doc.data_quality.confidence} Confidence
                </span>
              )}
            </div>
            <h4 className="text-base font-semibold text-gray-900">{doc.filename}</h4>
            <p className="text-sm text-gray-600 mt-1 line-clamp-2">{doc.executive_summary}</p>
          </div>
          <svg className={`w-5 h-5 text-gray-400 transition-transform flex-shrink-0 ml-4 ${expanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-100 p-5 space-y-5">
          {/* Key Findings */}
          {doc.key_findings?.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-gray-900 mb-2">Key Findings</h5>
              <ul className="space-y-1.5">
                {doc.key_findings.map((f, i) => (
                  <li key={`kf-${i}-${f.slice(0, 20)}`} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-blue-500 mt-0.5 flex-shrink-0">&#x2022;</span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Market Data */}
          {doc.market_data?.market_sizes?.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-gray-900 mb-2">Market Data</h5>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-1.5 pr-4 text-gray-500 font-medium">Value</th>
                      <th className="text-left py-1.5 pr-4 text-gray-500 font-medium">Context</th>
                      <th className="text-left py-1.5 text-gray-500 font-medium">Year</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.market_data.market_sizes.map((ms, i) => (
                      <tr key={`ms-${i}-${ms.value}`} className="border-b border-gray-100">
                        <td className="py-1.5 pr-4 font-medium text-orange-600">{ms.value}</td>
                        <td className="py-1.5 pr-4 text-gray-700">{ms.context}</td>
                        <td className="py-1.5 text-gray-500">{ms.year || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Competitive Intelligence */}
          {doc.competitive_intelligence?.companies_mentioned?.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-gray-900 mb-2">Companies Mentioned</h5>
              <div className="flex flex-wrap gap-2">
                {doc.competitive_intelligence.companies_mentioned.map((c, i) => (
                  <span key={`co-${c}-${i}`} className="px-2.5 py-1 bg-gray-100 text-gray-700 text-xs rounded-full">{c}</span>
                ))}
              </div>
            </div>
          )}

          {/* Trends */}
          {doc.industry_trends?.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-gray-900 mb-2">Industry Trends</h5>
              <div className="space-y-2">
                {doc.industry_trends.map((t, i) => (
                  <div key={`trend-${i}-${(t.trend || '').slice(0, 20)}`} className="p-3 bg-gray-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-900">{t.trend}</p>
                    <p className="text-xs text-gray-600 mt-0.5">{t.description}</p>
                    {t.impact && <p className="text-xs text-orange-600 mt-0.5">Impact: {t.impact}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Strategic Insights */}
          {doc.strategic_insights?.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-gray-900 mb-2">Strategic Insights</h5>
              <ul className="space-y-1.5">
                {doc.strategic_insights.map((s, i) => (
                  <li key={`insight-${i}-${s.slice(0, 20)}`} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="text-green-500 mt-0.5 flex-shrink-0">&#x2713;</span>
                    <span>{s}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Quotable Facts */}
          {doc.quotable_facts?.length > 0 && (
            <div>
              <h5 className="text-sm font-semibold text-gray-900 mb-2">Key Statistics</h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {doc.quotable_facts.map((q, i) => (
                  <div key={`qf-${i}-${(q.fact || '').slice(0, 20)}`} className="p-3 bg-blue-50 rounded-lg border-l-3 border-blue-400">
                    <p className="text-sm font-medium text-gray-900">{q.fact}</p>
                    {q.page_context && <p className="text-xs text-gray-500 mt-0.5">{q.page_context}</p>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ResearchLibrary;
